<template>
  <errorhandler
    v-if="showProspectCallbackError || null"
    :prospect-callback-error-msg="prospectCallbackErrorMsg"
    @reset-show-prospect-callback-error="resetShowProspectCallbackError"
  />

  <!-- Create account Modal -->
  <div
    id="tSASetupModal"
    class="modal fade tsa-setup-modal tw-block"
    role="dialog"
    aria-labelledby="tSASetupModalLabel"
    data-backdrop="false"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <button
            type="button"
            class="close"
            aria-label="Close"
            @click="onClickClose"
          >
            <img src="@/assets/img/close.svg" />
          </button>
        </div>
        <div class="modal-body account-modal">
          <div class="modal-body-sec1">
            <span> {{ getUiText("user-mfa-register-heading-1") }} </span>
          </div>
          <div class="auth-modal-text">
            {{ getUiText("user-mfa-register-heading-sub-1") }}
          </div>

          <div>
            <div class="qr-code">
              <div class="qr-code-image">
                <img :src="imgSrc" />
              </div>
              <div class="qr-code-text">
                <div class="helper-text">
                  {{ getUiText("user-mfa-register-qr-prompt-1") }}
                </div>
                <div
                  class="token"
                  data-cy="twoStepAuthenticationSetup-secretToken"
                >
                  {{ secretToken }}
                </div>
              </div>
            </div>
          </div>
          <div class="form-group">
            <form>
              <label for="id" class="control-label input-label-form">{{
                getUiText("user-mfa-register-enter-code-heading-1")
              }}</label>
              <input
                ref="authcodeInput"
                v-model="authCode"
                data-cy="twoStepAuthenticationSetup-authCode"
                type="text"
                :class="`form-control form-control-lg authcode-input ${
                  !totpValid ? 'invalid-input' : ''
                }`"
                tabindex="0"
                @input="checkIsTokenValid"
              />
              <div
                v-if="!totpValid"
                class="error-msg"
                :class="!totpValid ? 'mg-top-19' : ''"
              >
                <span>{{ getUiText("user-mfa-setup-error-valid-1") }}</span>
              </div>
            </form>
          </div>
          <div class="letsgo-btn">
            <button
              type="button"
              data-cy="twoStepAuthenticationSetup-submit"
              class="btn btn-primary letsgo-button"
              :disabled="isCodeValid ? false : true"
              @click="submit"
            >
              {{ getUiText("user-mfa-register-button-lets-go-1") }}
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <div class="modal-footer-sec1">
            {{ getUiText("user-mfa-register-footer-text-1") }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import $ from "jquery";
import userService from "@/services/user/userService";
import errorhandler from "@/components/common/exceptionHandler.vue";
import Utils from "@/utils/utilities";
import { load } from "recaptcha-v3";
import store from "@/store";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import router from "@/router";
import { localStorageBoolean, localStorageKey } from "@/utils/constants";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { showErrorAPI } from "@/services/apiRoutes";

const imgSrc = ref("");
const authCode = ref("");
const isCodeValid = ref(false);
const totpValid = ref(true);
const secretToken = ref("");

const showProspectCallbackError = ref(false);
const prospectCallbackErrorMsg = ref("");

const languageSelected = computed(
  () => store.state.LanguagePicker.languageSelected,
);
const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);

const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);

const checkIsTokenValid = (e) => {
  if (e.target.value && e.target.value.length > 1 && isNaN(e.target.value)) {
    totpValid.value = false;
  } else {
    totpValid.value = true;
  }
  if (e.target.value && e.target.value.length >= 6 && !isNaN(e.target.value)) {
    isCodeValid.value = true;
  } else {
    isCodeValid.value = false;
  }
};

const getSetupInfo = async () => {
  try {
    await userService.getMFASetupInfo().then(
      (res: any) => {
        if (res?.data?.data && res?.data?.data?.dataURL) {
          imgSrc.value = res.data.data.dataURL;
          secretToken.value = res.data.data.tempSecret;
        }
      },
      (error: any) => {
        if (error && error.data && error.data.error && error.data.error.type) {
          if (error.data.error.type === "exc_session_invalid-1") {
            sessionStorage.setItem(
              "prospectCallbackErrorInfo",
              JSON.stringify(error.data.error),
            );
            router.push({ path: "/" });
          } else {
            showErrorInfo(error.data.error);
          }
        } else {
          showErrorInfo(error.data);
        }
      },
    );
  } catch (error) {
    return error;
  }
};

const showErrorInfo = (errorInfo: any) => {
  if (errorInfo && errorInfo !== "") {
    errorInfo =
      typeof errorInfo === "string" ? JSON.parse(errorInfo) : errorInfo;
    if (errorInfo && Object.keys(errorInfo).length > 0 && errorInfo.message) {
      showProspectCallbackError.value = true;
      prospectCallbackErrorMsg.value = errorInfo.message;
    }
  }
};

const showToastError = (message) => {
  const emitData: IToastMessage = {
    variant: ToastVariant.ERROR,
    message,
  };
  EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
};

const authcodeInput: any = ref();
const onResize = () => {
  authcodeInput.value && authcodeInput.value.focus();
};

const setLanguages = async () => {
  await Utils.getV3Recaptcha().then(
    async (finalKey: any) => {
      localStorage.removeItem("csrfReloadDone");
      const captchKey = finalKey;
      const recaptcha = await load(`${captchKey}`, {
        autoHideBadge: true,
      });
      recaptcha.execute("submit").then((token) => {
        localStorage.setItem(localStorageKey.recaptcha, token);
        store.dispatch("LanguagePicker/setLanguages", token);
      });
      recaptcha.execute("submit").then((token) => {
        localStorage.setItem(localStorageKey.recaptcha, token);
        store.dispatch("LanguagePicker/setLanguageList", token);
      });
    },
    (error: any) => {
      if (error && error.data && error.data.error && error.data.error.type) {
        if (error.data.error.type === "exc_session_invalid-1") {
          sessionStorage.setItem(
            "prospectCallbackErrorInfo",
            JSON.stringify(error.data.error),
          );
          router.push({ path: "/" });
        } else {
          showErrorInfo(error.data.error);
        }
      } else if (error.data.message === "invalid csrf token") {
        if (localStorage.getItem("csrfReloadDone") == null) {
          localStorage.setItem("csrfReloadDone", localStorageBoolean.true);
          router.go(0);
        }
        sessionStorage.setItem(
          "prospectCallbackErrorInfo",
          JSON.stringify(error.data),
        );
        router.push({ path: "/" });
      } else {
        showErrorInfo(error.data);
      }
    },
  );
};

onMounted(() => {
  getSetupInfo();
  nextTick(function () {
    window.addEventListener("resize", onResize);
    onResize();
  });

  document.getElementById("tSASetupModal")?.classList.add("show");

  if (!$("body").hasClass("page-opacity")) {
    $("body").addClass("page-opacity page-bg");
  }

  setLanguages();
});

const getUiText = (id: string, isArray = false) => {
  return Utils.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const submit = () => {
  if (authCode.value && totpValid.value) {
    userService.confirmMFAToken({ token: authCode.value }).then(
      async (res: any) => {
        if (res?.data?.message) {
          await Utils.afterLoginActions(res.data.data);
        }
      },
      (error: any) => {
        if (error?.data?.error?.type) {
          if (error.data.error.type === "exc_session_invalid-1") {
            sessionStorage.setItem(
              "prospectCallbackErrorInfo",
              JSON.stringify(error.data.error),
            );
            router.push({ path: "/" });
          } else if (error.data.error.type === "exc_user_auth_locked_out") {
            const message = getUiText("app-api-user-mfa-verify-error-1");
            showToastError(message);
          } else {
            if (error.data.error.type === "exc_user_mfa_enable_fail-1") {
              showToastError(
                getUiText("app-api-user-mfa-verify-fail-experied-1"),
              );
              return;
            }
            if (
              error.data.error.type === "exc_invalid_parameters-1" &&
              error.data.error.id === "token"
            ) {
              showToastError(
                getUiText("app-api-user-mfa-verify-invalid-param-1"),
              );
              return;
            }
            showErrorAPI(error, "error");
          }
        } else {
          showErrorAPI(error, "error");
        }
      },
    );
  }
};

const resetShowProspectCallbackError = () => {
  showProspectCallbackError.value = false;
  prospectCallbackErrorMsg.value = "";
};

const onClickClose = () => {
  router.go(-1);
};

onBeforeUnmount(() => {
  if ($("body").hasClass("page-opacity")) {
    $("body").removeClass("page-opacity page-bg page-shadow");
  }
});
</script>

<style lang="scss" scoped>
.tsa-setup-page {
  background: #0345bd;

  .form-control:focus {
    box-shadow: none;
  }

  .form-control::placeholder {
    color: #b7b7b7;
    font-weight: 300;
    font-size: 22px;
    line-height: 33px;
  }

  .tsa-setup-modal {
    height: 100%;
    width: 100%;
    position: absolute;
    left: 50%;
    top: 50%;
    pointer-events: none;
    transform: translate(-50%, -50%);

    .modal-dialog {
      //transition: none;
      min-width: 630px;
    }

    .modal-header {
      border: none;
      margin: 0px 0px 0px 10px;
      justify-content: flex-end;

      .close {
        float: right;
        opacity: 1;
        margin-right: 10px;
        margin-top: 10px;
        position: relative;
        padding: 0;
        z-index: 999;

        img {
          width: 19px;
          height: 19px;
        }
      }
    }

    .modal-content {
      box-shadow: 2px 0px 20px rgba(33, 31, 88, 0.1);
      border-radius: 6px;
      border: none;
    }

    .modal-body {
      padding: 20px 70px 48px 70px;

      .flex-container {
        position: absolute;
        bottom: 0px;
        height: 100%;
      }

      .modal-body-sec1 {
        color: #0345bd;
        font-size: 26px;
        font-weight: 600;
        line-height: 39px;
      }

      .auth-modal-text {
        font-size: 15px;
        color: #231f20;
        line-height: 22px;
        font-weight: 400;
        margin-top: 15px;
      }

      .letsgo-btn {
        margin-top: 35px;
        display: flex;
        align-items: center;
      }

      .input-label-form {
        font-weight: 500;
        font-size: 12px;
        color: #231f20;
        line-height: 18px;
      }

      .authcode-input {
        border: none;
        font-style: normal;
        font-weight: 500;
        font-size: 22px;
        line-height: 33px;
        color: #0345bd;
        border-left: 1px solid rgba(183, 183, 183, 0.3);
        border-radius: unset;
        letter-spacing: 0.25em;
      }

      .form-group {
        margin-top: 40px;

        label {
          margin-bottom: 14px;
        }
      }

      .letsgo-button {
        width: 132px;
        height: 45px;
        border-radius: 29.4638px;
        background: #0345bd;
        font-style: normal;
        font-weight: 600;
        font-size: 18px;
        line-height: 27px;
        box-shadow: 0px 2.18987px 4.37975px rgba(33, 31, 88, 0.25);
        text-transform: uppercase;
      }

      .letsgo-btn button:disabled {
        color: #ffffff;
        background: #eeeff0;
        border-color: #eeeff0;
      }

      .forgot-text-button {
        font-size: 12px;
        line-height: 27px;
        padding-left: 30px;
        text-decoration-line: underline;
        color: #0345bd;
      }
    }

    .modal-footer {
      background: linear-gradient(
        180deg,
        rgba(230, 230, 230, 0.38) 0%,
        #ffffff 58.06%
      );
      padding: 26px 65px;

      .modal-footer-sec1 {
        line-height: 20.4px;
        font-size: 12px;
      }
    }

    .qr-code {
      display: flex;
      align-items: center;
      padding: 15px 15px 15px 15px;
      margin-top: 40px;
      background: rgba(238, 239, 240, 0.5);
      border: 1px solid #eeeff0;
      box-sizing: border-box;
      border-radius: 4px;
    }

    .qr-code-image {
      background-color: #ffffff;
      padding: 15px;
      border-radius: 4px;

      img {
        width: 155px;
        height: 150px;
      }
    }

    .qr-image {
      position: relative;
      top: 3px;
    }

    .qr-code-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      word-break: break-word;
      padding-left: 30px;
    }

    .helper-text {
      font-size: 12px;
      line-height: 20px;
    }

    .token {
      font-weight: 600;
      font-size: 18px;
      line-height: 27px;
      text-transform: uppercase;
      margin-left: 0px;
      position: relative;
      margin-top: 10px;
    }
  }

  .modal {
    z-index: 1 !important;
  }
}

@media (max-width: 767px) {
  .tsa-setup-page {
    .tsa-setup-modal {
      .modal-header {
        .close {
          margin-top: 3px !important;

          img {
            width: 17px;
            height: 17px;
          }
        }
      }

      .modal-dialog {
        min-width: unset;
      }

      .modal-body {
        padding: 13px 30px 30px 30px;

        .modal-body-sec1 {
          font-size: 22px;
          line-height: 34.5px;
        }

        .form-group {
          margin-top: 24px !important;
        }

        .letsgo-button {
          width: 136px;
          font-size: 16px;
          line-height: 24px;
        }

        .authcode-input {
          font-size: 18px;
          padding-right: 0px;
        }

        .input-label-form {
          font-size: 12px;
          line-height: 18px;
        }

        .forgot-text-button {
          font-size: 12px;
          line-height: 18px;
        }

        .letsgo-btn {
          margin-top: 20px;
        }

        .auth-modal-text {
          font-size: 15px !important;
          line-height: 22px !important;
          margin-top: 8px !important;
        }

        .modal-body-sec1 span {
          display: none;
        }

        .modal-body-sec1:after {
          content: "TFA Authentication";
        }
      }

      .modal-footer {
        padding: 26px 25px;
      }
    }
  }
}

@media (max-height: 500px) {
  .modal {
    padding-top: 50px;
  }
}

@media (min-width: 3840px) {
  .tsa-setup-page {
    .tsa-setup-modal {
      .form-control:focus {
        padding-left: 30px;
        font-size: 38px;
        line-height: 30px;
      }

      .modal-dialog {
        max-width: 1090px !important;
      }

      .modal-body {
        padding: 45px 90px 90px 90px;
      }

      .modal-content {
        border-radius: 15px !important;
      }

      .modal-body-sec1 {
        font-size: 52px !important;
        line-height: 78px !important;
      }

      .form-group {
        margin-top: 35px !important;
      }

      .auth-modal-text {
        font-size: 28px !important;
        line-height: 44px !important;
      }

      .input-label-form {
        font-size: 24px !important;
        line-height: 36px !important;
        margin-bottom: 28px !important;
      }

      .forgot-text-button {
        font-size: 24px !important;
        line-height: 36px !important;
        margin-left: 40px !important;
      }

      .close {
        margin-right: 35px !important;
        margin-top: 40px !important;

        img {
          height: 30px !important;
          width: 30px !important;
          zoom: 1.4;
        }
      }

      .letsgo-btn {
        margin-top: 76px !important;
      }

      .authcode-input {
        font-size: 44px !important;
        padding-left: 30px;
        border-left: 2px solid rgba(183, 183, 183, 0.3) !important;
      }

      .letsgo-button {
        width: 200px !important;
        height: 90px !important;
        font-size: 30px !important;
        border-radius: 41px !important;
      }

      .modal-footer-sec1 {
        line-height: 40.8px !important;
      }

      .modal-footer {
        font-size: 21px !important;
        padding: 35px 105px;
      }
    }
  }
}
</style>
